import { useState, useRef, useEffect, useCallback } from 'react';

export const useWineImageZoom = (slug) => {
  const [canHoverZoom, setCanHoverZoom] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const imageContainerRef = useRef(null);
  const lastTouchRef = useRef({ x: 0, y: 0 });
  const lastPinchDistRef = useRef(0);
  const fullScreenImageRef = useRef(null);
  const lastTapRef = useRef(0);

  useEffect(() => {
    setIsHovering(false);
    setIsFullScreen(false);
  }, [slug]);

  useEffect(() => {
    const checkAmplifySupport = () => {
      const hasMouseZoom = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const touchPrimary = window.matchMedia('(pointer: coarse)').matches;
      const isTouchTablet = navigator.maxTouchPoints > 1;
      setCanHoverZoom(hasMouseZoom && !touchPrimary && !isTouchTablet);
    };
    checkAmplifySupport();
    const queries = [
      window.matchMedia('(hover: hover) and (pointer: fine)'),
      window.matchMedia('(pointer: coarse)'),
    ];
    const onChange = () => checkAmplifySupport();
    queries.forEach((mq) => {
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', onChange);
      } else {
        mq.addListener(onChange);
      }
    });
    return () => {
      queries.forEach((mq) => {
        if (typeof mq.removeEventListener === 'function') {
          mq.removeEventListener('change', onChange);
        } else {
          mq.removeListener(onChange);
        }
      });
    };
  }, []);

  const handleMouseMove = (e) => {
    if (!imageContainerRef.current) {
      return;
    }
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const openFullScreen = () => {
    if (!canHoverZoom) {
      return;
    }
    resetZoom();
    setIsFullScreen(true);
  };

  const closeFullScreen = useCallback(() => {
    setIsFullScreen(false);
    resetZoom();
  }, [resetZoom]);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      lastPinchDistRef.current = dist;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && isDragging && scale > 1) {
      const deltaX = e.touches[0].clientX - lastTouchRef.current.x;
      const deltaY = e.touches[0].clientY - lastTouchRef.current.y;
      setPosition((prev) => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (lastPinchDistRef.current > 0) {
        const scaleDiff = (dist - lastPinchDistRef.current) * 0.01;
        setScale((prev) => Math.min(Math.max(prev + scaleDiff, 1), 4));
      }
      lastPinchDistRef.current = dist;
    }
  }, [isDragging, scale]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    lastPinchDistRef.current = 0;
    if (scale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      if (scale > 1) {
        resetZoom();
      } else {
        setScale(2.5);
      }
    }
    lastTapRef.current = now;
  }, [scale, resetZoom]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeFullScreen();
      }
    };
    const preventScroll = (e) => {
      if (isFullScreen) {
        e.preventDefault();
      }
    };
    if (isFullScreen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.addEventListener('touchmove', preventScroll, { passive: false });
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('touchmove', preventScroll);
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, [isFullScreen, closeFullScreen]);

  return {
    canHoverZoom,
    isHovering,
    setIsHovering,
    mousePosition,
    isFullScreen,
    scale,
    position,
    isDragging,
    imageContainerRef,
    fullScreenImageRef,
    handleMouseMove,
    openFullScreen,
    closeFullScreen,
    resetZoom,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleDoubleTap,
  };
};
